/**
 * SessionExpiryWarning - PROJ-035: Sliding Session Enhancement
 *
 * Toast/banner component that shows different warnings for:
 * - Idle expiration: "Session expiring due to inactivity. Move your mouse to stay logged in."
 * - Absolute expiration: "Maximum session duration reached. You will need to re-login."
 */

import { useEffect, useState, useCallback } from 'react';
import { useSessionEvents } from '@/hooks/useTokenManager';
import { getRefreshToken } from '@/lib/auth';
import { useTokenManager, type TokenExpiration } from '@/hooks/useTokenManager';

export function SessionExpiryWarning() {
  const [warningType, setWarningType] = useState<'idle' | 'absolute' | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const { getTokenExpiration } = useTokenManager();

  // Listen for expiry warnings
  useSessionEvents({
    onIdleExpiringSoon: useCallback(() => {
      setWarningType('idle');
    }, []),

    onAbsoluteExpiringSoon: useCallback(() => {
      setWarningType('absolute');
    }, []),

    onRefreshed: useCallback(() => {
      // Clear warning when session refreshes
      setWarningType(null);
      setSecondsRemaining(null);
    }, []),

    onExpired: useCallback(() => {
      // Clear warning when session expires (modal will show)
      setWarningType(null);
      setSecondsRemaining(null);
    }, []),
  });

  // Update seconds remaining periodically
  useEffect(() => {
    if (!warningType) {
      setSecondsRemaining(null);
      return;
    }

    const updateTimer = () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        setSecondsRemaining(null);
        return;
      }

      const expiration: TokenExpiration | null = getTokenExpiration(refreshToken);
      if (!expiration) {
        setSecondsRemaining(null);
        return;
      }

      if (warningType === 'idle') {
        setSecondsRemaining(expiration.secondsUntilIdle);
      } else if (warningType === 'absolute') {
        setSecondsRemaining(expiration.secondsUntilAbsolute);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000); // Update every second

    return () => clearInterval(interval);
  }, [warningType, getTokenExpiration]);

  if (!warningType || secondsRemaining === null) {
    return null;
  }

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;

  return (
    <div
      className="fixed top-4 right-4 z-50 max-w-md p-4 rounded-lg shadow-lg animate-in slide-in-from-top-2"
      style={{
        backgroundColor: warningType === 'idle' ? '#fef3c7' : '#fecaca',
        borderLeft: `4px solid ${warningType === 'idle' ? '#f59e0b' : '#ef4444'}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {warningType === 'idle' ? (
            <svg
              className="w-6 h-6 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
        </div>

        <div className="flex-1">
          <p className="font-semibold text-sm" style={{ color: warningType === 'idle' ? '#92400e' : '#991b1b' }}>
            {warningType === 'idle'
              ? 'Sesión expirando por inactividad'
              : 'Duración máxima de sesión alcanzada'}
          </p>
          <p className="mt-1 text-sm" style={{ color: warningType === 'idle' ? '#78350f' : '#7f1d1d' }}>
            {warningType === 'idle' ? (
              <>
                Tu sesión expirará en <strong>{minutes}m {seconds}s</strong>.{' '}
                Mueve el mouse o interactúa con la página para mantener la sesión activa.
              </>
            ) : (
              <>
                Tu sesión expirará en <strong>{minutes}m {seconds}s</strong>.{' '}
                Deberás iniciar sesión nuevamente después.
              </>
            )}
          </p>
        </div>

        <button
          onClick={() => {
            setWarningType(null);
            setSecondsRemaining(null);
          }}
          className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
          aria-label="Cerrar"
        >
          <svg
            className="w-5 h-5"
            style={{ color: warningType === 'idle' ? '#92400e' : '#991b1b' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
